// The two views of Hub ▸ Marketing (CEO 2026-10-22: "Profit and margin aren't
// a Marketing system — they don't need to see it").
//   "gp"      — admin + finance: gross profit, POAS, LTV (unchanged);
//   "revenue" — marketing-only viewers: the same screens in revenue, ROAS and
//               first-year revenue. The API sends them no profit field at
//               all (apps/api/src/services/marketing-no-profit.ts); these
//               helpers only pick the right words and columns.
import type { Measure } from "./types";

export const isRevenue = (m: Measure | undefined | null) => m === "revenue";

/** What the money column and the return-per-ad-dollar column are called. */
export const WORDS: Record<Measure, { value: string; valueLower: string; ratio: string; ratioLong: string }> = {
  gp: { value: "Gross profit", valueLower: "gross profit", ratio: "POAS", ratioLong: "Gross profit per ad dollar (POAS)" },
  revenue: { value: "Revenue", valueLower: "revenue", ratio: "ROAS", ratioLong: "Revenue per ad dollar (ROAS)" },
};

/**
 * Where a return-per-ad-dollar turns red / green. POAS: below 1.00 loses
 * money, 2.00 and up is strong. ROAS — the same bars as the API's
 * ROAS_THRESHOLDS (marketing-overview.ts; the gate checks they agree): below
 * 3.00 under target, 5.00 and up strong.
 */
export const RATIO_BARS: Record<Measure, { low: number; high: number }> = { gp: { low: 1, high: 2 }, revenue: { low: 3, high: 5 } };

type Valued = { gpCents?: number | null; revenueCents?: number | null; poas?: number | null; roas?: number | null };
/** The row's money (gross profit or revenue, by the view). */
export const valueOf = (r: Valued, m: Measure): number | null => (m === "revenue" ? r.revenueCents ?? null : r.gpCents ?? null);
/** The row's return per ad dollar (POAS or ROAS, by the view). */
export const ratioOf = (r: Valued, m: Measure): number | null => (m === "revenue" ? r.roas ?? null : r.poas ?? null);

/** How the Signal column decides, in words. */
export function signalRule(m: Measure): string {
  const b = RATIO_BARS[m];
  const r = WORDS[m].ratio;
  return `With 2 or more paid orders: Scale = ${r} ${b.high.toFixed(2)} or more; Hold = ${b.low.toFixed(2)}–${b.high.toFixed(2)}; Cut = below ${b.low.toFixed(2)}. With fewer than 2: Too early to tell under $300 spent; otherwise Watch (business clients order weeks after the click) — except $1,000 or more spent with no order at all, which is Cut.`;
}
